import { useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import type { Location } from 'react-router-dom';
import { Button, Card, Form, Input, Typography, App as AntdApp } from 'antd';

import { login } from '../api/endpoints';
import { useAuth } from '../hooks/useAuth';
import { LIMITS } from '../data/limits';

const { Title, Text } = Typography;

// SHAR-1093/1094/1099 — after login go back to the page the admin was sent
// away from (AuthGate passes it as `state.from`), e.g. a booking link opened
// from an email. No target, or a target that is the login page itself, falls
// back to the bookings list.
function returnPath(state: unknown): string {
  const from = (state as { from?: Partial<Location> } | null)?.from;
  const pathname = from?.pathname;
  if (!pathname || pathname === '/login') return '/bookings';
  return `${pathname}${from?.search ?? ''}${from?.hash ?? ''}`;
}

export function LoginPage() {
  const setSession = useAuth((s) => s.setSession);
  const nav = useNavigate();
  const location = useLocation();
  const { message } = AntdApp.useApp();
  const [busy, setBusy] = useState(false);
  // Regression 28 Sep — /login while signed in goes to the app, not a second
  // login form. (A stale token is caught by the app's own session check and
  // lands back here with the token cleared.)
  // Read once, at mount: the token this page's own login sets must not trigger
  // it (that navigation is onFinish's).
  const [signedInOnArrival] = useState(() => !!useAuth.getState().token);

  const onFinish = async ({ email, password }: { email: string; password: string }) => {
    setBusy(true);
    try {
      const r = await login(email, password);
      setSession(r.token, r.admin);
      nav(returnPath(location.state), { replace: true });
    } catch (e: any) {
      message.error(e?.response?.data?.message || 'Login failed');
    } finally {
      setBusy(false);
    }
  };

  if (signedInOnArrival) return <Navigate to={returnPath(location.state)} replace />;

  return (
    <div
      style={{
        minHeight: '100vh',
        background:
          'linear-gradient(135deg, #0F2A4A 0%, #1B3A66 60%, #2C5181 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
      }}
    >
      <Card
        style={{ width: 360 }}
        styles={{ body: { padding: '32px 28px' } }}
      >
        <div style={{ textAlign: 'center', marginBottom: 24 }}>
          <div
            style={{
              width: 44,
              height: 44,
              background: '#0F2A4A',
              borderRadius: 10,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#fff',
              fontWeight: 800,
              fontSize: 18,
            }}
          >
            SJ
          </div>
          <Title level={3} style={{ marginTop: 16, marginBottom: 4 }}>
            ShareJet Admin
          </Title>
          <Text type="secondary">Sign in to continue</Text>
        </div>
        <Form layout="vertical" onFinish={onFinish}>
          <Form.Item
            label="Email"
            name="email"
            rules={[
              { required: true, message: 'Email is required' },
              { type: 'email', message: 'Enter a valid email' },
            ]}
          >
            <Input maxLength={LIMITS.email} placeholder="admin@sharejet.demo" autoComplete="email" />
          </Form.Item>
          <Form.Item
            label="Password"
            name="password"
            rules={[{ required: true, message: 'Password is required' }]}
          >
            <Input.Password autoComplete="current-password" />
          </Form.Item>
          <Button type="primary" htmlType="submit" loading={busy} block>
            Log in
          </Button>
        </Form>
      </Card>
    </div>
  );
}
